///
// @file photo-replace.js
// @desc Lets the doctor replace a photo the patient uploaded, from the review
//       screen, running the same AI validation used on the patient side.
//
// Deliberately standalone. It does NOT reuse modules/shared/uploader.js, and the
// markup hooks on `[data-doctor-dropzone]` — load_drop_zone() in
// user_dashboard.coffee only selects `[data-dropzone]`, so nothing here can
// reach the patient upload flow.
//
// Flow: DirectUpload -> POST /dashboard/validate_photo -> Sidekiq/Bedrock ->
// verdict over PhotoValidationChannel.
//
// The field pointer is only rewritten on commit(), never on upload. A photo the
// AI rejects therefore cannot silently become the field value — the doctor has
// to accept it explicitly. Same reason the form is blocked while a validation
// is in flight.
///

import { DirectUpload } from "@rails/activestorage"
import Dropzone from "dropzone"
import consumer from "channels/consumer"

// Matches the patient side: large phone photos need ~7s of MiniMagick
// preprocessing plus ~5s of Bedrock, so anything under this is too tight.
var AI_TIMEOUT_MS = 30000
var VALIDATE_URL = "/dashboard/validate_photo"

class DoctorPhotoReplace {

  constructor(app, config) {
    this.app = app

    this.init = this.init.bind(this)
    this.toggle_panel = this.toggle_panel.bind(this)
    this.accept_rejected = this.accept_rejected.bind(this)
    this.retry = this.retry.bind(this)
    this.guard_submit = this.guard_submit.bind(this)

    this.app.register_turbolinks_load_handler(this.init)

    $(document).on("click", "[data-doctor-replace-toggle]", this.toggle_panel)
    $(document).on("click", "[data-doctor-replace-accept]", this.accept_rejected)
    $(document).on("click", "[data-doctor-replace-retry]", this.retry)
    $(document).on("submit", ".application-doctor-review", this.guard_submit)
  }


  init() {
    if (!$("body").hasClass("doctors-applications")) { return }

    $("[data-doctor-dropzone]").each((i, el) => {
      var $dz = $(el)
      // Turbolinks re-runs load handlers; don't stack Dropzone instances.
      if ($dz.data("dz-initialized")) { return }
      $dz.data("dz-initialized", true)
      this.build_dropzone($dz)
    })
  }


  // -- Panel toggles (replace panel and original-photo history) --------------
  toggle_panel(e) {
    e.preventDefault()
    var $toggle = $(e.currentTarget)
    var $panel = $toggle.siblings("[data-doctor-replace-panel]")
    $panel.prop("hidden", !$panel.prop("hidden"))
    $toggle.toggleClass("is-open", !$panel.prop("hidden"))
  }


  // -- Block submit while a validation is still running ----------------------
  guard_submit(e) {
    if ($("[data-doctor-dropzone].ai-validating").length === 0) { return true }
    e.preventDefault()
    window.alert("Espera a que termine la validación de la foto antes de continuar.")
    return false
  }


  build_dropzone($dz) {
    var self = this
    var is_ai = $dz.data("ai-validation") ? true : false

    // Per-dropzone state. `blob` holds the uploaded-but-not-yet-committed photo.
    var state = {
      $dz: $dz,
      $field: $dz.closest(".form-field"),
      $actions: $dz.closest("[data-doctor-photo-replace]").find("[data-doctor-replace-actions]"),
      is_ai: is_ai,
      blob: null,
      committed_blob_id: null,
      subscription: null,
      timeout: null
    }
    $dz.data("replace-state", state)

    var dropzone = new Dropzone($dz.get(0), {
      url: $("meta[name=direct-upload-url]").attr("content"),
      acceptedFiles: is_ai ? ".jpg,.jpeg,.png" : ".jpg,.jpeg,.png,.pdf",
      maxFiles: 1,
      // Never null: Dropzone compares `file.size > maxFilesize * 1024 * 1024`,
      // and null collapses to 0, which rejects every file.
      maxFilesize: is_ai ? 6 : 20,
      parallelUploads: 1,
      uploadMultiple: false,
      autoProcessQueue: false, // DirectUpload handles the transfer, not Dropzone
      addRemoveLinks: false,
      createImageThumbnails: true,
      thumbnailWidth: 180,
      thumbnailHeight: 180,
      thumbnailMethod: "contain",
      dictInvalidFileType: "El formato del archivo no es válido",
      dictFileTooBig: "El archivo es muy grande ({{filesize}}MB). Máximo permitido: {{maxFilesize}}MB.",
      dictMaxFilesExceeded: "No puedes subir más fotos",

      // Dropzone only reaches this callback once size, type and maxFiles all
      // pass, so the upload never starts on a file it would have rejected.
      accept: function(file, done) {
        done()
        self.upload(state, file)
      }
    })

    state.dropzone = dropzone

    // Runs before accept(), so the slot is free by the time maxFiles is checked.
    dropzone.on("addedfile", function(file) {
      dropzone.files.filter(function(f) { return f !== file }).forEach(function(old) {
        dropzone.removeFile(old)
      })
    })

    dropzone.on("error", function(file, message) {
      dropzone.removeFile(file)
      self.set_status(state, "ai-rejected",
        '<p><i class="ai-error"></i> ' + (typeof message === "string" ? message : "No se pudo usar este archivo") + "</p>")
    })
  }


  upload(state, file) {
    var self = this

    self.reset_verdict(state)
    self.set_status(state, "ai-validating", '<i class="ai-spinner"></i> Subiendo foto...')

    var upload = new DirectUpload(file, $("meta[name=direct-upload-url]").attr("content"), {
      directUploadWillStoreFileWithXHR: function(request) {
        request.upload.addEventListener("progress", function(event) {
          var progress = Math.round((event.loaded * 100) / event.total)
          state.$dz.find(".progress-load").css("width", progress + "%")
          state.$dz.find(".status").html("Subiendo documento: <strong>" + progress + "%</strong>")
        })
      }
    })

    upload.create(function(error, blob) {
      state.$dz.find(".progress-load").css("width", "0%")

      if (error) {
        state.$dz.addClass("dropzone-area--has-error")
        self.set_status(state, "ai-rejected", '<p><i class="ai-error"></i> No se pudo subir el archivo. Intenta de nuevo.</p>')
        return
      }

      state.blob = { id: blob.id, signed_id: blob.signed_id }
      state.$dz.removeClass("dropzone-area--has-error")
      state.$dz.find(".dz-preview").show()

      if (state.is_ai) {
        self.validate(state)
      } else {
        // No AI configured for this field — the doctor's upload stands as-is.
        self.commit(state, "Foto reemplazada")
      }
    })
  }


  validate(state) {
    var self = this
    var blob_id = state.blob.id

    self.set_status(state, "ai-validating", '<i class="ai-spinner"></i> Procesando foto...')

    state.subscription = consumer.subscriptions.create(
      { channel: "PhotoValidationChannel", blob_id: blob_id },
      {
        received: function(data) {
          // Intermediate steps — keep spinning.
          if (data.status === "preprocessing" || data.status === "validating" || data.status === "correcting") {
            state.$dz.find(".status").html('<i class="ai-spinner"></i> ' + data.message)
            return
          }

          self.clear_wait(state)

          if (data.status === "approved" || data.status === "corrected") {
            self.commit(state, data.message || "Foto aceptada", data.status === "corrected")
          } else {
            self.reject(state, data.message, data.instructions)
          }
        }
      }
    )

    // If the verdict never arrives, keep the photo rather than losing the
    // doctor's upload — same fallback the patient flow uses.
    state.timeout = setTimeout(function() {
      if (state.$dz.hasClass("ai-validating")) {
        self.clear_wait(state)
        self.commit(state, "Foto aceptada")
      }
    }, AI_TIMEOUT_MS)

    $.ajax({
      url: VALIDATE_URL,
      method: "POST",
      headers: { "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content") },
      data: {
        field_id: state.$dz.data("ai-field-id"),
        application_id: state.$dz.data("application-id"),
        blob_id: state.blob.signed_id
      },
      dataType: "json",
      success: function(response) {
        if (response.status === "skipped") {
          self.clear_wait(state)
          self.commit(state, "Foto aceptada")
        }
        // "processing" — the verdict comes over ActionCable.
      },
      error: function(xhr) {
        self.clear_wait(state)

        if (xhr.status === 429) {
          var retry_after = parseInt(xhr.getResponseHeader("Retry-After")) || 60
          self.set_status(state, "ai-rejected",
            '<p><i class="ai-error"></i> <strong>Límite de validaciones alcanzado</strong></p>' +
            '<p class="ai-instructions">Espera ' + retry_after + 's y vuelve a subir la foto.</p>')
          state.$dz.addClass("ai-cooldown")
          setTimeout(function() { self.reset_dropzone(state) }, retry_after * 1000)
        } else {
          // Server-side problem, not the doctor's fault — keep the photo.
          self.commit(state, "Foto aceptada (no se pudo validar con IA)")
        }
      }
    })
  }


  // -- Verdict: accepted -----------------------------------------------------
  // Repoints the field at the new blob and archives the previous one. This is
  // the only place the metadata pointer is touched.
  commit(state, message, corrected) {
    // Guard against committing twice (double click on "usar de todas formas",
    // or a verdict landing right as the timeout fires) — that would push the
    // new blob into the history and leave the field pointing at itself.
    if (!state.blob || state.committed_blob_id === state.blob.id) { return }
    state.committed_blob_id = state.blob.id

    var $meta = state.$field.find("input[data-file-blob-id]").first()
    var $prev = state.$field.find("[data-doctor-prev-blobs]").first()
    var previous_value = $meta.val()

    if (previous_value) {
      $prev.val($prev.val() ? $prev.val() + ", " + previous_value : previous_value)
    }

    $meta.val(state.blob.id).attr("data-file-blob-id", state.blob.id)

    // Attach on save. replace_on_assign_to_many is false, so this appends. One
    // input per committed blob, never reused: a photo moved into the history
    // must stay attached or its preview would 404 on the next page load.
    $meta.after($('<input type="hidden" autocomplete="off" name="application[files][]">').val(state.blob.signed_id))

    state.$actions.prop("hidden", true)
    this.set_status(state, corrected ? "ai-corrected" : "ai-approved",
      '<i class="ai-check"></i> ' + message + ' — se guardará al enviar la solicitud')
  }


  // -- Verdict: rejected -----------------------------------------------------
  // The doctor is the clinical authority, so this is a warning, not a wall:
  // they can accept the photo anyway or upload a different one.
  reject(state, message, instructions) {
    var html = '<p><i class="ai-error"></i> ' + (message || "Fotografía con errores") + "</p>"
    if (instructions) {
      html += '<p class="ai-instructions">' + instructions + "</p>"
    }
    this.set_status(state, "ai-rejected", html)
    state.$actions.prop("hidden", false)
  }


  accept_rejected(e) {
    e.preventDefault()
    var state = $(e.currentTarget).closest("[data-doctor-photo-replace]").find("[data-doctor-dropzone]").data("replace-state")
    if (!state || !state.blob) { return }
    this.commit(state, "Foto aceptada por el médico")
  }


  retry(e) {
    e.preventDefault()
    var state = $(e.currentTarget).closest("[data-doctor-photo-replace]").find("[data-doctor-dropzone]").data("replace-state")
    if (!state) { return }
    this.reset_dropzone(state)
  }


  // -- Helpers ---------------------------------------------------------------
  set_status(state, css_class, html) {
    state.$dz
      .removeClass("ai-validating ai-approved ai-corrected ai-rejected ai-cooldown")
      .addClass(css_class)
    state.$dz.find(".status").addClass("uploading").html(html)
  }


  clear_wait(state) {
    if (state.timeout) { clearTimeout(state.timeout); state.timeout = null }
    if (state.subscription) { state.subscription.unsubscribe(); state.subscription = null }
  }


  // Clears the pending verdict without touching an already committed photo.
  reset_verdict(state) {
    this.clear_wait(state)
    state.blob = null
    state.$actions.prop("hidden", true)
  }


  reset_dropzone(state) {
    this.reset_verdict(state)
    state.dropzone.removeAllFiles(true)
    state.$dz.removeClass("ai-validating ai-approved ai-corrected ai-rejected ai-cooldown dropzone-area--has-error")
    state.$dz.find(".progress-load").css("width", "0%")
    state.$dz.find(".status").removeClass("uploading").html('Arrastrar y colocar aquí o <span>buscar</span>')
  }
}

// ESM export, not module.exports: this file uses `import`, so webpack treats it
// as an ES module. user_dashboard.coffee imports it the same way it does
// modules/shared/uploader.js.
export default DoctorPhotoReplace
